import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as api from 'requests'
import { parseUrlQuery } from 'utilities'

const appSettingCache = {}
export const useAppSetting = (name, { cache } = {}) => {
  const [setting, setSetting] = useState(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const isPending = useRef(false)

  useEffect(() => {
    if (!isPending.current) {
      if (cache ? !appSettingCache[name] : true) {
        isPending.current = true
        setError(false)
        setSetting(null)
        setLoading(true)
        api.GET_APP_SETTING_BY_NAME(
          name,
          (setting) => {
            if (setting) {
              appSettingCache[name] = setting
              isPending.current = false
              setSetting(setting)
              setLoading(false)
            }
          },
          (err) => {
            console.error(err)
            setError(err)
            setLoading(false)
          }
        )
      } else {
        setLoading(false)
        setError(false)
        setSetting(appSettingCache[name])
      }
    }
  }, [name])

  return {
    loading,
    error,
    setting,
  }
}

export const useList = (initial) => {
  const [list, setList] = useState(() => {
    if (Array.isArray(initial)) {
      return initial
    } else {
      throw new Error('The initial value for useList must be an array.')
    }
  })

  const push = (item) => {
    setList((l) => [...l, item])
  }

  const pop = (item) => {
    setList((l) => [item, ...l])
  }

  const addAtIndex = (item, index) => {
    setList((l) => [...l.slice(0, index), item, ...l.slice(index)])
  }

  const removeAtIndex = (index) => {
    setList((l) => [...l.slice(0, index), ...l.slice(index + 1)])
  }

  const replaceAtIndex = (item, index) => {
    setList((l) => [...l.slice(0, index), item, ...l.slice(index + 1)])
  }

  return [list, { push, pop, removeAtIndex, addAtIndex, replaceAtIndex }]
}

export const usePrevious = (value) => {
  const ref = useRef()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export const useUser = () => {
  const selectUser = useCallback((state) => (state.auth.loggedIn ? state.auth : null), [])
  const user = useSelector(selectUser)
  return user
}

export const useUserHasRight = (right) => {
  const user = useUser()
  const hasRight = useMemo(() => {
    if (user) {
      user.rights.includes(right)
    } else {
      return false
    }
  }, [user, hasRight])
  return hasRight
}

export const useUserHasAllRights = (rights) => {
  const user = useUser()
  const hasRight = useMemo(() => {
    if (user) {
      return rights.every((right) => user.rights.includes(right))
    } else {
      return false
    }
  }, [user, hasRight])
  return hasRight
}

export const useUserHasAnyRights = (rights) => {
  const user = useUser()
  const hasRight = useMemo(() => {
    if (user) {
      return rights.some((right) => user.rights.includes(right))
    } else {
      return false
    }
  }, [user, hasRight])
  return hasRight
}

export const useSearchParams = (search = '') => {
  const params = useMemo(() => {
    return parseUrlQuery(search)
  }, [search])
  return params
}

/*
takes a request function from requests.js, and returns the data
requestFunction: (successCallback, errorCallback) => void
the default request functions already have this schema, but if 
the request function has more params, you can pass in an 
anonymous function with the custom params bound. for example:

const data = useRequestData((category, id, successCallback, errorCallback) => REQUEST_FUNCTION('exampleCategory', 'exampleId', successCallback, errorCallback))

*/
export const useRequestData = (requestFunction, dependancyArray = [], defaultState = null) => {
  const [data, setData] = useState(defaultState)

  useEffect(() => {
    let ignore = false
    requestFunction(
      (res) => {
        if (!ignore) {
          setData(res)
        }
      },
      (err) => {
        if (!ignore) {
          console.error(err)
        }
      }
    )
    return () => {
      ignore = true
    }
  }, [...dependancyArray])

  return data
}

export const useInterval = (callback, delay) => {
  const intervalRef = useRef(null);
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    const tick = () => savedCallback.current();
    if (typeof delay === 'number') {
      intervalRef.current = window.setInterval(tick, delay);
      return () => window.clearInterval(intervalRef.current);
    }
  }, [delay]);
  return intervalRef;
}

export const useDebounce = (value, milliSeconds) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, milliSeconds)

    return () => {
      clearTimeout(handler)
    }
  }, [value, milliSeconds])

  return debouncedValue
}